import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { CONTACT_EMAIL } from "@/lib/site-data";

// Use current page origin in the browser so the API call always matches the
// hostname the user is on (avoids www vs non-www CORS preflight redirect issues).
// Falls back to REACT_APP_BACKEND_URL during SSR/build.
const API_BASE = typeof window !== "undefined" && window.location && window.location.origin
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE}/api`;

const Contact = () => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status.state === "loading") return;
    setStatus({ state: "loading", msg: "" });

    try {
      await axios.post(`${API}/contact`, form, { timeout: 20000 });
      setStatus({
        state: "success",
        msg: "Thank you! Sade will reply within 2 business days.",
      });
      setForm({ first_name: "", last_name: "", email: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong sending your message. Please try again or email us directly.";
      setStatus({ state: "error", msg: detail });
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-brand-cream"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <p className="font-sans text-xs md:text-sm tracking-[0.25em] uppercase text-brand-sage mb-5">
            Let's work together
          </p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-brand-ink">
            Tell us about{" "}
            <span className="italic text-brand-blue">your goals.</span>
          </h2>
          <p className="mt-6 text-lg text-brand-ink/75 leading-relaxed">
            Whether you have a clear vision or a tangled set of priorities, we'd
            love to hear from you. We reply within 2 business days.
          </p>
          <div className="mt-8 pt-8 border-t border-brand-blue/15">
            <p className="text-xs tracking-[0.2em] uppercase text-brand-sage mb-2">Direct</p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="contact-direct-email"
              className="font-serif text-xl text-brand-blue hover:text-brand-ink transition-colors break-all"
            >
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          data-testid="contact-form"
          className="lg:col-span-7 bg-white rounded-[2rem] border border-brand-blue/15 p-8 md:p-10 shadow-xl shadow-brand-blue/5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field
              label="First name"
              name="first_name"
              value={form.first_name}
              onChange={handleChange}
              testid="contact-first-name"
              required
            />
            <Field
              label="Last name"
              name="last_name"
              value={form.last_name}
              onChange={handleChange}
              testid="contact-last-name"
              required
            />
          </div>
          <div className="mt-5">
            <Field
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              testid="contact-email"
              required
            />
          </div>
          <div className="mt-5">
            <label className="block">
              <span className="text-xs tracking-[0.2em] uppercase text-brand-sage font-medium">
                Message
              </span>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={5}
                required
                minLength={5}
                placeholder="What are you building? Where are you stuck? Anything you'd like us to know…"
                data-testid="contact-message"
                className="mt-2 w-full bg-transparent border-0 border-b border-brand-blue/25 focus:border-brand-blue focus:ring-0 outline-none py-2 text-brand-ink placeholder:text-brand-ink/30 font-sans resize-none transition-colors"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={status.state === "loading"}
            data-testid="contact-submit-btn"
            className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brand-blue text-brand-cream hover:bg-brand-ink disabled:opacity-60 disabled:cursor-not-allowed transition-all rounded-full px-8 py-4 font-sans font-medium text-base shadow-md shadow-brand-blue/20"
          >
            {status.state === "loading" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send className="w-4 h-4" /> Send message
              </>
            )}
          </button>

          {status.state === "success" && (
            <div
              data-testid="contact-success"
              className="mt-6 flex items-start gap-3 p-4 rounded-2xl bg-brand-periwinkle/30 border border-brand-blue/20 text-brand-ink"
            >
              <CheckCircle2 className="w-5 h-5 text-brand-blue flex-shrink-0 mt-0.5" />
              <p className="text-sm">{status.msg}</p>
            </div>
          )}
          {status.state === "error" && (
            <div
              data-testid="contact-error"
              className="mt-6 flex items-start gap-3 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-900"
            >
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{status.msg}</p>
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
};

const Field = ({ label, name, type = "text", value, onChange, testid, required }) => (
  <label className="block">
    <span className="text-xs tracking-[0.2em] uppercase text-brand-sage font-medium">
      {label}
    </span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="mt-2 w-full bg-transparent border-0 border-b border-brand-blue/25 focus:border-brand-blue focus:ring-0 outline-none py-2 text-brand-ink placeholder:text-brand-ink/30 font-sans transition-colors"
    />
  </label>
);

export default Contact;
